import { Config, Store } from '../config';
import { Company, CompanyKey, companies, markets } from './organizations';

/**
 * The home page's schema.org graph: the site, the company that runs it, and the owners above it.
 *
 * One `@graph` rather than a script per node, so the nodes can point at each other by `@id` - the
 * same `@id` on every market, so Google reads one company with five storefronts rather than five
 * companies that happen to share a name.
 */

type JsonLdNode = Record<string, unknown>;

/** the companies that run a storefront of their own, and are therefore an OnlineStore */
const operators = new Set<CompanyKey>(Object.values(markets).flatMap((market) => (market ? [market.company] : [])));

const companyType = (key: CompanyKey) => (operators.has(key) ? 'OnlineStore' : 'Organization');

const identifierOf = (company: Company): JsonLdNode => ({
	'@type': 'PropertyValue',
	propertyID: company.identifier.propertyID,
	value: company.identifier.value,
	name: company.identifier.name,
});

const addressOf = (company: Company): JsonLdNode | undefined =>
	company.address && { '@type': 'PostalAddress', ...company.address };

/**
 * An owner further up the chain. It carries what identifies the company - founding date included -
 * and who owns it, and leaves the address, contact details and description to the market page the
 * company runs itself, except for the group, which runs no store and would otherwise go undescribed.
 */
function ownerNode(key: CompanyKey): JsonLdNode {
	const company = companies[key];
	const runsAStore = operators.has(key);
	return {
		'@type': companyType(key),
		'@id': company.id,
		name: company.name,
		legalName: company.legalName,
		url: company.url,
		identifier: identifierOf(company),
		vatID: company.vatID,
		foundingDate: company.foundingDate,
		address: runsAStore ? undefined : addressOf(company),
		sameAs: company.sameAs,
		parentOrganization: company.parent ? { '@id': companies[company.parent].id } : undefined,
	};
}

export function buildHomeGraph(store: Store): JsonLdNode[] | null {
	const market = markets[store];
	if (!market) return null;

	const company = companies[market.company];
	const home = company.url;
	const storeNode: JsonLdNode = {
		'@type': 'OnlineStore',
		'@id': company.id,
		name: market.name,
		legalName: company.legalName,
		alternateName: company.alternateName,
		url: home,
		logo: new URL(`images/${market.logo}`, home).href,
		description: market.description,
		foundingDate: company.foundingDate,
		vatID: company.vatID,
		identifier: identifierOf(company),
		address: addressOf(company),
		telephone: market.telephone,
		email: market.email,
		contactPoint: {
			'@type': 'ContactPoint',
			contactType: 'customer support',
			telephone: market.telephone,
			email: market.email,
			availableLanguage: market.language,
			areaServed: company.address?.addressCountry,
		},
		sameAs: company.sameAs,
		parentOrganization: company.parent ? { '@id': companies[company.parent].id } : undefined,
	};

	const website: JsonLdNode = {
		'@type': 'WebSite',
		'@id': `${home}#website`,
		name: market.name,
		alternateName: market.alternateName,
		url: home,
		publisher: { '@id': company.id },
	};

	const owners: JsonLdNode[] = [];
	for (let key = company.parent; key; key = companies[key].parent) owners.push(ownerNode(key));

	return [website, storeNode, ...owners];
}

/**
 * Whether the host being served is the market's own site.
 *
 * A store is not one host: ondio.se answers for the Swedish market and carries the rebrand
 * landing page, while Sweden itself has not renamed and its canonical site is
 * kontorsgiganten.se. Publishing the Kontorsgiganten graph on that host would describe another
 * site, so it publishes none. The legacy brands kontorgiganten.no and worky.dk fall out the same
 * way and answer 308 to the Ondio host anyway, while the stage hosts sit under the canonical
 * domain and the dev ones under kgx.se, where seeing the graph is the point.
 */
function onTheMarketsOwnSite(host: string, canonicalUrl: string): boolean {
	const site = new URL(canonicalUrl).host.replace(/^www\./, '');
	const name = host.split(':')[0];
	return name === site || name.endsWith(`.${site}`) || name === 'localhost' || name.endsWith('.kgx.se');
}

/**
 * JSON for a `<script>` element. `JSON.stringify` leaves `<` alone, and a `</script>` inside a
 * string would end the element early, so the one character that can do that is escaped.
 */
export function buildHomeJsonLd(config: Config): string | null {
	const market = markets[config.store];
	if (!market || !onTheMarketsOwnSite(config.domain, companies[market.company].url)) return null;

	const graph = buildHomeGraph(config.store);
	if (!graph) return null;
	return JSON.stringify({ '@context': 'https://schema.org', '@graph': graph }).replace(/</g, '\\u003c');
}
